"use client";

import type { JSX } from "react";
import { Select, Text, View } from "@/components";
import { useLayout } from "@/providers";
import { getMunicipalities, getProvinces } from "@/utils/elearning/geo";

/** Valor centinela (no vacío) para la opción "sin selección" del `Select`. */
const NONE = "__none__";

export interface ProvinceMunicipalitySelectProps {
  /** Provincia elegida (nombre del catálogo) o `null` si no hay selección. */
  province: string | null;
  /** Municipio elegido (nombre del catálogo) o `null` si no hay selección. */
  municipality: string | null;
  /** Se dispara con la selección resultante ante cualquier cambio. */
  onChange: (next: {
    province: string | null;
    municipality: string | null;
  }) => void;
  /** Deshabilita ambos selects (p. ej. mientras se guarda). */
  disabled?: boolean;
}

/**
 * @name ProvinceMunicipalitySelect
 * @description Selección en cascada provincia → municipio sobre el catálogo estático
 * de Argentina (`@/utils/elearning/geo`). Al elegir provincia se resetea el municipio;
 * el select de municipio queda deshabilitado hasta que haya provincia. Reemplaza al
 * antiguo texto libre del "municipio de presentación" del certificado, normalizando el
 * dato. Compone dos moléculas `Select` (sin tocar átomos/moléculas).
 *
 * @param {ProvinceMunicipalitySelectProps} props - Valores controlados y callback.
 * @returns {JSX.Element} Los dos selects encadenados.
 */
const ProvinceMunicipalitySelect = ({
  province,
  municipality,
  onChange,
  disabled = false
}: ProvinceMunicipalitySelectProps): JSX.Element => {
  const { t } = useLayout();

  const provinceItems = [
    { id: NONE, label: t("selectProvince"), value: NONE },
    ...getProvinces().map((name) => ({ id: name, label: name, value: name }))
  ];

  const municipalityNames = province ? getMunicipalities(province) : [];
  const municipalityItems = [
    {
      id: NONE,
      label: province
        ? t("selectMunicipality")
        : t("municipalityNeedsProvince"),
      value: NONE
    },
    ...municipalityNames.map((name) => ({
      id: name,
      label: name,
      value: name
    }))
  ];

  const handleProvince = (value: string) => {
    // Cambiar de provincia invalida el municipio previo.
    onChange({ province: value === NONE ? null : value, municipality: null });
  };

  const handleMunicipality = (value: string) => {
    onChange({ province, municipality: value === NONE ? null : value });
  };

  return (
    <View className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <View className="flex flex-col gap-2">
        <Text
          type="label"
          htmlFor="provincia"
          className="text-sm font-medium text-foreground"
        >
          {t("province")}
        </Text>
        <Select
          id="provincia"
          items={provinceItems}
          selected={province ?? NONE}
          setSelected={handleProvince}
          disabled={disabled}
          // text-base en mobile: Safari iOS hace zoom al enfocar un control de
          // menos de 16px (la clase `input` del template usa text-xs) y no vuelve.
          className="w-full text-base sm:text-xs"
        />
      </View>
      <View className="flex flex-col gap-2">
        <Text
          type="label"
          htmlFor="municipio"
          className="text-sm font-medium text-foreground"
        >
          {t("municipality")}
        </Text>
        <Select
          id="municipio"
          items={municipalityItems}
          selected={municipality ?? NONE}
          setSelected={handleMunicipality}
          disabled={disabled || !province}
          className="w-full text-base sm:text-xs"
        />
      </View>
    </View>
  );
};

export default ProvinceMunicipalitySelect;
